import { Plus, X, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import aws_ai_practitioner from '../../../assets/images/aws_ai-practitioner.png';
import aws_cloud_practitioner from '../../../assets/images/aws_cloud_practitioner.png';
import { setOpenCard } from '../../../redux/Slices/CloudPrepSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/store/hooks';

const SET_OPEN_CARD = 'SET_OPEN_CARD';

function AWSCard() {
  const dispatch = useAppDispatch();

  const { openCard } = useAppSelector(state => state.root.cloudPrep);

  const handleToggle = (card: string) => {
    dispatch(setOpenCard(openCard === card ? null : card));
  };

  const navigate = useNavigate();
  const handleClickCloudPractitioner = () => {
    const company = 'AMAZON_WEB_SERVICES';
    const testType = 'AWS_CLOUD_PRACTITIONER';

    dispatch(setOpenCard(testType));
    navigate(`/cloud-prep-tabs/${company}/${testType}`);
  };

  const handleClickAIPractitioner = () => {
    const company = 'AMAZON_WEB_SERVICES';
    const testType = 'AWS_AI_PRACTITIONER';

    dispatch(setOpenCard(testType));
    navigate(`/cloud-prep-tabs/${company}/${testType}`);
  };

  return (
    <div className='flex flex-col items-center justify-center gap-8 p-6 md:flex-row'>
      {/* ---------- CARD 1 ---------- */}
      <div className='group relative h-[420px] w-80 cursor-pointer'>
        <div className=' absolute -inset-1 rounded-2xl bg-gradient-to-r from-orange-400 via-pink-500 via-purple-500 to-blue-500 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-75'></div>
        <div className='relative h-full w-full rounded-2xl bg-white shadow-md'>
          {openCard !== 'cloud' ? (
            <div
              onClick={handleClickCloudPractitioner}
              className='flex flex-col'
            >
              {/* Image wrapper */}
              <div className='overflow-hidden rounded-lg'>
                <img
                  src={aws_cloud_practitioner}
                  alt='AWS Certified Cloud Practitioner'
                  className='h-full w-full object-contain transition-transform duration-300 group-hover:scale-110'
                />
              </div>

              <h2 className='mt-4 px-6 text-lg font-semibold text-gray-900'>
                AWS Certified Cloud Practitioner
              </h2>
              <p className='mt-2 px-6 text-sm text-gray-600'>
                Showcase foundational knowledge of AWS cloud services and cloud
                computing
              </p>

              {/* Learn More Button (arrow always visible, text slides in) */}
              <div className='group relative mt-5 flex w-full items-center justify-start overflow-hidden p-6'>
                {/* Text slides in */}
                <span className='absolute left-6 -translate-x-4 transform text-sm font-medium text-gray-800 opacity-0 transition-all duration-500 ease-out group-hover:translate-x-0 group-hover:opacity-100'>
                  Learn more
                </span>

                {/* Arrow starts at beginning and moves slightly on hover */}
                <ArrowRight className='mt-[4px] h-4 w-4 text-gray-800 transition-transform duration-500 ease-in-out group-hover:translate-x-20' />
              </div>
            </div>
          ) : (
            <div className='flex h-full flex-col p-6 text-left'>
              <div className='mb-3 flex items-center justify-between '>
                <h3 className='text-lg font-semibold text-green-700'>
                  About this exam
                </h3>

                <button
                  onClick={() => handleToggle('cloud')}
                  className='rounded-full bg-gray-100 p-2 transition hover:bg-gray-200'
                >
                  {openCard === 'cloud' && (
                    <X className='h-5 w-5 text-gray-800' />
                  )}
                </button>
              </div>
              <p className='mb-6 text-sm leading-relaxed text-gray-700'>
                AWS Certified Cloud Practitioner validates foundational
                knowledge of AWS Cloud, its global infrastructure, and pricing
                models. It’s ideal for individuals seeking an understanding of
                AWS Cloud concepts without technical experience.
              </p>
              <div className='mt-auto'>
                <button className='group mt-auto flex items-center gap-2 rounded-full bg-gray-900 px-4 py-2 font-medium text-white transition'>
                  Learn more
                  <ArrowRight className='h-4 w-4 transform transition-transform duration-300' />
                </button>
              </div>
            </div>
          )}

          {/* Toggle Button */}
          {openCard !== 'cloud' && (
            <button
              onClick={() => handleToggle('cloud')}
              className='absolute bottom-4 right-4 rounded-full bg-primary p-2 transition hover:bg-blue_a12 hover:bg-gray-200'
            >
              <Plus className='h-5 w-5 text-white' />
            </button>
          )}
        </div>
      </div>

      {/* ---------- CARD 2 ---------- */}
      <div className='group relative h-[420px] w-80 cursor-pointer'>
        {/* Gradient Glow */}
        <div className='absolute -inset-1 rounded-2xl bg-gradient-to-r from-orange-400 via-pink-500 via-purple-500 to-blue-500 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-75'></div>

        {/* White Card */}
        <div className='relative h-full w-full rounded-2xl bg-white shadow-md'>
          {openCard !== 'ai' ? (
            <div
              onClick={handleClickAIPractitioner}
              className='z-10 flex h-full flex-col'
            >
              <div className='overflow-hidden rounded-t-2xl'>
                <img
                  src={aws_ai_practitioner}
                  alt='AWS Certified AI Practitioner'
                  className='h-full w-full object-contain transition-transform duration-300 group-hover:scale-110'
                />
              </div>

              <h2 className='mt-4 px-6 text-lg font-semibold text-gray-900'>
                AWS Certified AI Practitioner
              </h2>
              <p className='mt-2 px-6 text-sm text-gray-600'>
                Unlock new career possibilities with this AI certification.
              </p>

              {/* Learn More Button */}
              <div className='group relative mt-5 flex w-full items-center justify-start overflow-hidden p-6'>
                <span className='absolute left-6 -translate-x-4 transform text-sm font-medium text-gray-800 opacity-0 transition-all duration-500 ease-out group-hover:translate-x-0 group-hover:opacity-100'>
                  Learn more
                </span>
                <ArrowRight className='mt-[4px] h-4 w-4 text-gray-800 transition-transform duration-500 ease-in-out group-hover:translate-x-20' />
              </div>
            </div>
          ) : (
            <div className='flex h-full flex-col p-6 text-left'>
              <div className='mb-3 flex items-center justify-between'>
                <h3 className='text-lg font-semibold text-green-700'>
                  About this exam
                </h3>
                <button
                  onClick={() => handleToggle('ai')}
                  className='rounded-full bg-gray-100 p-2 transition hover:bg-gray-200'
                >
                  <X className='h-5 w-5 text-gray-800' />
                </button>
              </div>
              <p className='mb-6 text-sm leading-relaxed text-gray-700'>
                AWS Certified AI Practitioner validates foundational knowledge
                of artificial intelligence (AI) and machine learning (ML)
                concepts on the AWS Cloud. It’s ideal for individuals seeking an
                understanding of AI/ML services, their use cases, and basic
                implementation strategies without requiring deep technical
                expertise.
              </p>
              <div className='mt-auto'>
                <button className='group mt-auto flex items-center gap-2 rounded-full bg-gray-900 px-4 py-2 font-medium text-white transition'>
                  Learn more
                  <ArrowRight className='h-4 w-4 transform transition-transform duration-300' />
                </button>
              </div>
            </div>
          )}

          {/* Toggle Button */}
          {openCard !== 'ai' && (
            <button
              onClick={() => handleToggle('ai')}
              className='absolute bottom-4 right-4 rounded-full bg-primary p-2 transition hover:bg-blue_a12'
            >
              <Plus className='h-5 w-5 text-white' />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AWSCard;
